import type { Messages } from './ja';

export const en: Messages = {
  meta: {
    title: '3D Map Editor',
  },
  menu: {
    play: 'Click to play',
    view: 'Spectate',
    graphicsMenu: 'Graphics settings',
    room: 'Room',
    join: 'Join',
    playerName: 'Player name',
    change: 'Apply',
    sensitivity: 'Mouse sensitivity',
    graphics: 'Graphics quality',
    depthOfField: 'Depth of field',
    sunSdfShadows: 'Sun SDF',
    diSdfShadows: 'DI SDF',
    giSdfShadows: 'GI SDF',
    sdfAnalytic: 'SDF analytic',
    sdfAnalyticBvh: 'SDF BVH',
    sdfVoxelSpheres: 'SDF voxel spheres',
    sunlight: 'Sunlight',
    ambient: 'Indirect light',
    giDiffuseGain: 'GI diffuse gain',
    giDistanceAmbient: 'Far ambient fill',
    giNearAmbient: 'Near ambient fill',
    denoiser: 'Point-light denoiser',
    restirCheapVisibility: 'ReSTIR cheap candidate shadows',
    collisionDebug: 'Collision only',
    language: 'Language',
    players: 'Players in this room',
    controlsMenu: 'How to play',
    credits: 'Credits',
    textureCredit: 'Textures',
  },
  status: {
    connecting: 'Connecting…',
    open: 'Connected',
    closed: 'Disconnected (reconnecting automatically)',
  },
  graphics: {
    kicker: 'GRAPHICS',
    title: 'Graphics settings',
    closeHint: 'to close',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    on: 'On',
    off: 'Off',
    denoiserOff: 'Off',
    denoiserTemporal: 'Temporal only',
    denoiserSvgf: 'SVGF',
    denoiserSvgfSharp: 'SVGF sharp',
    denoiserRelax: 'RELAX',
    reset: 'Reset to defaults',
  },
  controls: {
    kicker: 'CONTROLS',
    title: 'How to play',
    closeHint: 'to close',
  },
  playerlist: {
    room: 'Room',
    self: '{name} (you)',
    menuHeading: '{label} ({count})',
    emptyConnecting: 'Connecting…',
    emptyClosed: 'Disconnected (reconnecting)',
  },
  viewHint: 'Spectating · Click to play · Esc for menu',
  mobile: {
    jump: 'Jump',
    moveAria: 'Move',
  },
  inventory: {
    kicker: 'CREATIVE LOADOUT',
    title: 'Inventory',
    closeHint: 'to close',
    catalogAria: 'Placeable items',
    instruction: 'Drag items onto the hotbar below to equip them.',
    tablistAria: 'Item types',
    tabBrace: 'Braces',
    tabPlane: 'Planes',
    tabPolygon: 'Polygons',
    tabStair: 'Stairs',
    tabObject: 'Objects',
    tabTool: 'Tools',
    tabTexture: 'Create texture',
    previewAria: 'Preview of the texture being created',
    allFaces: 'Same on all 6 faces',
    name: 'Name',
    list: 'List',
    newListName: 'New list name',
    listPlaceholder: 'e.g. Stone',
    color: 'Color',
    pattern: 'Pattern',
    resolution: 'Resolution',
    noiseScale: 'Noise scale',
    noiseContrast: 'Noise contrast',
    motif: 'Pattern seed',
    reroll: '⚄ Reroll noise',
    create: 'Create',
    selected: 'Selected',
    slot: 'Slot {n}',
    hotbar: 'Hotbar',
    invertHotbarScroll: 'Invert hotbar scroll',
    dragAria: 'Drag {label} to the hotbar',
    tool: 'Tool',
    empty: 'Empty',
    emptyNote: 'Fixed empty hand. Items cannot be placed here. No highlight or placement preview.',
    destroyNote: 'Click the object under the crosshair to destroy it.',
    rangeDestroyNote: 'Click two aim points snapped to half a grid tile. Objects overlapping that grid-aligned box are destroyed.',
    copyPasteNote: 'Click two corners of a grid-aligned box to copy everything the box overlaps, using the same volumes the crosshair hits. Corners are the raw aim points, not snapped. Clicking the same point twice copies only the object under the crosshair at the first click.',
    clipboardNote: 'Place the copied objects as one AABB. The bottom sits on the floor, the top lines up with the ceiling. Rotation follows look direction in 0° / 90° / 180° / 270° steps. Press Q to return to copy mode.',
    braceBendNote: 'Click a brace or plane edge, then a point on the side the curve should bulge. That edge becomes a minor arc. A right-isosceles click makes a 90° arc. On a brace the control point is projected onto the perpendicular bisector plane; on a plane it stays on the in-plane bisector line.',
    extrudeNote: 'Click a plane or brace. A plane grows along its normal; a brace becomes a paper strip from the nearest vertex to the second click. Bend and extrude can coexist. Click near the profile or vertex to clear.',
    extrudeUpNote: 'Click a plane or a coplanar brace to extrude the snap length toward you. A plane grows along its normal; a two-point brace grows only up or down; a polyline brace grows along its plane normal. Standing above a floor pushes up; below pushes down; a wall protrudes toward you. Each click adds another snap length.',
    extrudeDownNote: 'Click a plane or a coplanar brace to extrude the snap length away from you. A plane grows along its normal; a two-point brace grows only up or down; a polyline brace grows along its plane normal. Standing above a floor pushes down; below pushes up; a wall recedes away from you. Each click adds another snap length.',
    holeNote: 'Draw a closed polyline on a plane, an extruded plane, or an extruded brace face. Click the start point to punch a hole through the solid along the face normal. The outline must stay inside the face.',
    clothSimNote: 'Click a filled plane or a brace path to open the cloth settings. Volume puffs the solid; a sheet hangs from a pinned edge. A brace can sag like a wire with both ends pinned. The original shape stays. Click again to re-bake with different settings.',
    revolveNote: 'Click an edge of a filled plane or a coplanar brace to revolve it 360° around that edge. A fill becomes a solid; a path becomes a surface. Click the same edge again to flatten it. Extruded, holed, or cloth-baked shapes are skipped.',
    objectNote: 'Click a surface to place. Furniture stays upright; fixtures mount to the face.',
    width: 'Width',
    height: 'Height',
    rotation: 'Rotation (degrees)',
    thickness: 'Thickness',
    snap: 'Snap',
    light: 'Light',
    water: 'Water',
    newTexture: 'New texture',
    newList: 'New list…',
    slotTitle: '{n}: {title}',
    materialTitle: '{material} / {shape}',
  },
  shape: {
    brace: 'Brace',
    plane: 'Plane',
    polygon: 'Polygon',
    stair: 'Stairs',
    object: 'Object',
  },
  object: {
    'box-upright': 'Chair',
    'box-fixture': 'Light fixture',
  },
  tool: {
    empty: 'Empty',
    destroy: 'Destroy',
    rangeDestroy: 'Range destroy',
    copyPaste: 'Copy/paste',
    clipboard: 'Copy',
    clipboardCount: 'Copy {count}',
    braceBend: 'Bend',
    extrude: 'Extrude',
    extrudeUp: 'Extrude up',
    extrudeUpSnap: 'Extrude up {snap}',
    extrudeDown: 'Extrude down',
    extrudeDownSnap: 'Extrude down {snap}',
    hole: 'Hole punch',
    clothSim: 'Cloth sim',
    revolve: 'Revolve',
  },
  toast: {
    notConnected: 'Not connected to the server.',
    rangeNeedSurface: 'Aim at a grid, plane, or brace face.',
    rangeDestroyFirstPoint: 'Range destroy: first corner set. Aim at the opposite corner and click.',
    rangeDestroyTooLarge: 'Range is too large (max {max} m³).',
    copyPasteFirstPoint: 'Copy/paste: reference set. Aim at the opposite corner and click.',
    copyPasteEmpty: 'Nothing to copy.',
    copyPasteTooLarge: 'Range is too large (max {max} m³).',
    braceBendNeedBrace: 'Aim at a brace or plane edge to bend it.',
    braceBendFirstPoint: 'Bend: edge selected. Click the side the curve should bulge.',
    braceBendDegenerate: 'That point cannot form an arc. Pick a point off the segment.',
    extrudeNeedTarget: 'Aim at a plane or brace to extrude it.',
    extrudeFirstPlane: 'Extrude: plane selected. Click the intended length along the normal.',
    extrudeFirstBrace: 'Extrude: vertex selected. Click where that vertex should go.',
    extrudeDegenerate: 'That point cannot extrude. Pick a point off the profile or vertex.',
    extrudeUpDegenerate: 'That cannot extrude toward you. Non-planar polylines and lengths past the limit are skipped.',
    extrudeDownDegenerate: 'That cannot extrude away from you. Non-planar polylines and lengths past the limit are skipped.',
    holeNeedTarget: 'Aim at a plane, extruded plane, or extruded brace face to punch a hole.',
    holeTooFewPoints: 'A hole needs at least 3 points.',
    holeOutside: 'Keep the hole inside the face.',
    holeDegenerate: 'That outline cannot punch a hole. Self-intersecting or zero-area polygons are skipped.',
    holeTooManyPoints: 'Too many hole vertices.',
    holeTooMany: 'This solid already has the maximum number of holes.',
    clothSimNeedFill: 'Aim at a filled plane or a brace to run cloth sim.',
    clothSimFailed: 'That shape cannot be simulated.',
    revolveNeedEdge: 'Aim at a filled plane or coplanar brace edge to revolve it.',
    revolveHasExtrude: 'Extruded shapes cannot revolve. Clear the extrude first.',
    revolveHasHoles: 'Shapes with holes cannot revolve.',
    revolveHasCloth: 'Cloth-baked shapes cannot revolve.',
    revolveNotPlanar: 'Non-planar polylines cannot revolve.',
    revolveArcAxis: 'An arc cannot be the axis. Pick a straight edge.',
    revolveCleared: 'Revolve cleared. Back to a flat shape.',
    revolveNoBend: 'A revolved shape cannot be bent. Clear the revolve first.',
    samePoint: 'Start and end are the same position.',
    braceNeedPoints: 'A brace path needs at least 2 points.',
    braceTooManyPoints: 'Too many brace vertices.',
    planeNeedPoints: 'A polygon needs at least 3 points.',
    planeTooManyPoints: 'Too many plane vertices.',
    notPlanar: 'The points are not coplanar.',
    colinear: 'The points are colinear, so a face cannot be made.',
    stairNeedHorizontal: 'Stairs need a horizontal span.',
    noDestroyTool: 'There is no destroy tool on the hotbar.',
    creatingTexture: 'Creating “{name}”…',
    createdTexture: 'Created “{name}”.',
    textureAdded: '“{name}” was added to the room.',
    peerJoined: '{name} joined',
    peerLeft: '{name} left',
    invalidRoom: 'Room names may only use letters, numbers, hyphens, and underscores (max 64).',
    roomRequiresLogin: 'You need to sign in to join rooms other than the lobby.',
    graphicsReset: 'Graphics settings reset to defaults',
  },
  item: {
    graphicsQuality: 'Graphics: {label}',
    depthOfField: 'Depth of field: {label}',
    sunSdfShadows: 'Sun SDF: {label}',
    diSdfShadows: 'DI SDF: {label}',
    giSdfShadows: 'GI SDF: {label}',
    sdfAnalytic: 'SDF analytic: {label}',
    sdfAnalyticBvh: 'SDF BVH: {label}',
    sdfVoxelSpheres: 'SDF voxel spheres: {label}',
    sunlight: 'Sunlight: {label}',
    ambient: 'Indirect light: {label}',
    giDiffuseGain: 'GI diffuse gain: {label}',
    giDistanceAmbient: 'Far ambient fill: {label}',
    giNearAmbient: 'Near ambient fill: {label}',
    denoiser: 'Denoiser: {label}',
    restirCheapVisibility: 'ReSTIR cheap candidate shadows: {label}',
    collisionDebug: 'Collision only: {label}',
    invertHotbarScroll: 'Hotbar scroll: {label}',
    flyOn: 'Flight mode ON',
    flyOff: 'Flight mode OFF',
    held: '{shape}: {label}  ({summary})',
  },
  build: {
    braceSummary: 'Thickness {thickness} / snap {snap} / {count} pts',
    planeSummary: 'Thickness {thickness} / snap {snap} / {mode} / {count} pts',
    planeModeRect: '3-point',
    polygonSummary: 'Thickness {thickness} / snap {snap} / {count} pts',
    enterToPlace: 'Enter to place',
    stairSummary: 'Snap {snap} / 3-point / {count} pts',
    objectSummary: 'Snap {snap}',
    clipboardSummary: 'Snap {snap} / {count} items',
    holeSummary: 'snap {snap} / {count} pts',
    gridTarget: 'Grid {i}, {j} / {side}',
    gridBelow: 'below',
    gridAbove: 'above',
    lightCount: 'Lights {count}',
    shapeLine: '{shape}  ·  {summary}',
  },
  gridEditor: {
    title: 'View grid',
    subtitle: 'A square lattice only you can see. It is not sent to others.',
    heightY: 'Height Y',
    snap: 'Snap',
    hide: 'Hide',
    walkOn: 'You can walk on top. You pass through from below.',
    invalidNumber: 'Enter a number.',
    hidden: 'Grid hidden. No lines or floor.',
    rotation: 'Rotation (degrees)',
    closeHint: 'to close',
  },
  clothSim: {
    title: 'Cloth simulation',
    subtitle: 'Bake a deformed mesh once. The original shape stays, and the resulting polygons are saved.',
    resolution: 'Resolution',
    steps: 'Steps',
    shape: 'Shape',
    shapeVolume: 'Volume',
    shapeSheet: 'Sheet',
    pin: 'Pin',
    pinNone: 'None',
    pinTop: 'Top',
    pinBottom: 'Bottom',
    pinLeft: 'Left',
    pinRight: 'Right',
    pinEnds: 'Both ends',
    gather: 'Gather',
    inflate: 'Inflate',
    gravity: 'Gravity',
    stiffness: 'Stiffness',
    preview: 'Preview',
    run: 'Run',
    running: 'Running…',
    cancel: 'Cancel',
    progress: '{percent}%',
    closeHint: 'to close',
  },
  texture: {
    defaultList: 'Default ({name})',
  },
  texturePattern: {
    plain: 'Plain',
    outline: '1px outline',
    'double-outline': 'Double outline',
    inset: 'Inset panel',
    'horizontal-stripes': 'Horizontal stripes',
    'vertical-stripes': 'Vertical stripes',
    'diagonal-stripes': 'Diagonal stripes',
    checker: 'Checker',
    grid: 'Grid',
    bricks: 'Bricks',
    planks: 'Planks',
    dots: 'Dots',
  },
  control: {
    touch: 'Phone: <b>stick</b> move / <b>Jump</b> on the right / drag anywhere else to look (no inventory)',
    move: '<b>W A S D</b> move / <b>Space</b> jump / <b>Shift</b> sneak (pass through when standing on the grid)',
    fly: '<b>Space×2</b> or <b>F</b> toggle flight (Space up, Shift down while flying)',
    sprint: '<b>Ctrl</b> or <b>W×2</b> to sprint',
    click: '<b>Click</b> (either button) uses the selected slot / <b>middle-click</b> copy material',
    hotbar: '<b>0–9</b> or <b>wheel</b> to change slots (slot 0 starts empty, slot 1 as the destroy tool)',
    destroy: '<b>X</b> switch to the destroy tool / <b>Z</b> return to the previous slot',
    inventory: '<b>E</b> open inventory / drag items and tools onto the hotbar',
    graphics: '<b>V</b> graphics settings',
    brace:
      '<b>Click</b> to place a brace as a polyline / <b>Enter</b> or click last vertex to finish, click start to close / <b>Backspace</b> undo last point / <b>Q</b> cancel drawing',
    plane:
      '<b>Click</b> three points to place a rectangular plane / <b>Backspace</b> undo last point / <b>Q</b> cancel drawing',
    polygon:
      '<b>Click</b> to place a polygon / <b>Enter</b> or click last vertex to finish, click start to close / <b>Backspace</b> undo last point / <b>Q</b> cancel drawing',
    stair: '<b>Click</b> three points to place stairs (first two for width, third for length) / <b>Backspace</b> undo last point / <b>Q</b> cancel drawing',
    thickness: '<b>Ctrl+wheel</b> or <b>[ ]</b> thickness / <b>G</b> view grid',
    extra: '<b>Tab</b> player list / <b>F3</b> debug',
    menu: '<b>Esc</b> this menu (also while spectating)',
  },
  material: {
    default: 'Concrete',
    wood: 'Wood',
    metal: 'Metal',
    brick: 'Brick',
    glass: 'Glass',
    grass: 'Grass',
    stone: 'Stone',
    accent: 'Accent',
    sand: 'Sand',
    light: 'Emissive',
    black: 'Black',
    scaffold: 'Scaffold',
    water: 'Water',
    'laminated-wood': 'Laminated wood',
    'metal-bump': 'Bumped metal',
    'metal-horizontal-bump': 'Horizontal bumped metal',
    'metal-horizontal': 'Horizontal metal',
    'terrazzo-curved': 'Curved terrazzo',
    'terrazzo-long': 'Long terrazzo',
    'terrazzo-rough': 'Rough terrazzo',
    'terrazzo-tiles': 'Terrazzo tiles',
    'weaved-basket': 'Woven basket',
    'concrete-stones-01': 'Concrete stones',
    'decorative-bricks-01': 'Decorative brick',
    'paint-copper-01': 'Painted copper',
    'plaster-01': 'Plaster',
    'sidewalk-brick-tiles-01': 'Sidewalk brick tiles 1',
    'sidewalk-brick-tiles-02': 'Sidewalk brick tiles 2',
    'sidewalk-brick-tiles-03': 'Sidewalk brick tiles 3',
    'sidewalk-tiles-01': 'Sidewalk tiles',
    'tiles-01': 'Tiles',
    'bricks-destroyed': 'Broken brick',
    'bricks-destroyed-plaster': 'Broken plaster brick',
    'colored-tiles-small': 'Small colored tiles',
    'fabric-square-patterrn': 'Square-pattern fabric',
    'ground-bricks-herringbone': 'Herringbone brick',
    'terrazo-tiles-decorative': 'Decorative terrazzo',
    'terrazo-tiles-gray': 'Gray terrazzo',
    'armor-scales-decorated': 'Decorative scale armor',
    'armor-scales-simple': 'Scale armor',
    'decorative-ceramic-tiles': 'Decorative ceramic tiles',
    'decorative-concrete-bricks': 'Decorative concrete brick',
    'decorative-fabric': 'Decorative fabric',
    'leather-cross': 'Cross leather',
    'leather-straps': 'Leather straps',
    'leather-straps-pattern': 'Leather strap pattern',
    'metal-grater': 'Grater metal',
    'ceramic-tiles-circular': 'Circular ceramic tiles',
    'dark-wood-floor-tiles': 'Dark wood floor tiles',
    'decorative-bathroom-tiles': 'Bathroom tiles',
    'decorative-black-tiles': 'Black tiles',
    'old-rug-brown': 'Old brown rug',
    'retractable-security-door': 'Security door',
    'window-grill': 'Window grill',
    'wodden-framed-window': 'Wood-framed window',
    'wodden-framed-windows': 'Wood-framed windows',
    'concrete-fish-tiles': 'Fish-pattern concrete',
    'leather-couchin-perfored': 'Perforated leather',
    'leather-couchin-perfored-diamond': 'Diamond perforated leather',
    'leather-couchin-rectangle': 'Square leather',
    'leather-couchin-scales': 'Scale leather',
    'metal-cooper-grid': 'Copper grid',
    'stylized-clover-ground': 'Clover ground',
    'terrazo-and-white-tiles': 'Terrazzo with white tiles',
    'tire-rubber': 'Tire rubber',
    'fabric-decorative-01': 'Decorative fabric 1',
    'fabric-decorative-02': 'Decorative fabric 2',
    'fabric-decorative-03': 'Decorative fabric 3',
    'fabric-scify-01': 'Sci-fi fabric',
    'fabric-wool-01': 'Wool',
    'floor-tiles-and-bricks': 'Tile and brick floor',
    'leather-couching-01': 'Quilted leather',
    'wall-plaster-stones': 'Plaster stone wall',
    'windows-blue-old': 'Old blue window',
    'metal-grid-clover': 'Metal grid (clover)',
    'metal-grid-cross': 'Metal grid (cross)',
    'metal-grid-spikes': 'Metal grid (spikes)',
    'metal-grid-square': 'Metal grid (square)',
    'metal-grid-square-white': 'Metal grid (white square)',
    'metal-grid-wave': 'Metal grid (wave)',
    'metal-shutter-cross-bars': 'Shutter (cross)',
    'metal-shutter-perfored-roller': 'Shutter (perforated)',
    'metal-shutter-perfored-roller-big': 'Shutter (large holes)',
    'metal-tiles-solded': 'Welded metal tiles',
    'metal-triangle-solded': 'Welded triangle metal',
    'metal-zinc-rough': 'Rough zinc',
    sponge: 'Sponge',
    'stone-floor-polished': 'Polished stone floor',
    'tiles-basic-01': 'Basic tiles',
    'tiles-diamond-moon': 'Diamond tiles',
    'tiles-marble-01': 'Marble tiles 1',
    'tiles-marble-02': 'Marble tiles 2',
    'concrete-destroyed-wall-01': 'Broken concrete wall',
    'old-bricks-01': 'Old brick',
    'stone-engrave-wall-01': 'Engraved stone wall',
    'wood-and-bricks-floor-01': 'Wood and brick floor',
    'wooden-floor-painted-01': 'Painted wood floor',
    'wooden-floor-01': 'Wood floor 1',
    'wooden-floor-02': 'Wood floor 2',
    'wooden-floor-03': 'Wood floor 3',
    'wooden-floor-04': 'Wood floor 4',
    'stone-tiles-01': 'Stone tiles 1',
    'stone-tiles-02': 'Stone tiles 2',
    'stone-tiles-03': 'Stone tiles 3',
    'stone-tiles-04': 'Stone tiles 4',
    'stone-tiles-05': 'Stone tiles 5',
    'stone-tiles-06': 'Stone tiles 6',
    'stone-tiles-07': 'Stone tiles 7',
    'stone-tiles-08': 'Stone tiles 8',
    'stone-tiles-09': 'Stone tiles 9',
    'base-white-tile': 'White tiles',
  },
  pack: {
    'mix-pack-02': 'Tiles & Metal',
    'mix-pack-01': 'Tiles & Stones',
    'mix-pack-03': 'Tiles & Roaches',
    'mix-pack-04': 'Leather & Metal',
    'mix-pack-05': 'Wood & Windows',
    'mix-pack-06': 'Mix Pack 06',
    'mix-pack-07': 'Mix Pack 07',
    'metal-pack-02': 'Metal Grids',
    'tiles-and-metals': 'Metal & Tiles',
    'wood-and-bricks': 'Bricks & Wood',
    'stone-tiles': 'Stone Tiles',
    'base-white-tile': 'White Tile',
  },
};
